import { useCallback } from 'react';
import { supabase } from '../supabaseClient';

// Génère un ID visiteur unique
const getVisitorId = () => {
  let id = localStorage.getItem('nb_vid');
  if (!id) {
    id = 'v_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    localStorage.setItem('nb_vid', id);
  }
  return id;
};

// Génère un ID de session (30 min)
const getSessionId = () => {
  const now = Date.now();
  let session = JSON.parse(localStorage.getItem('nb_sid') || '{}');
  
  if (!session.id || now - session.ts > 30 * 60 * 1000) {
    session = { id: 's_' + Math.random().toString(36).substr(2, 9), ts: now };
  } else {
    session.ts = now;
  }
  
  localStorage.setItem('nb_sid', JSON.stringify(session));
  return session.id;
};

// Détecte l'appareil
const getDeviceInfo = () => {
  const ua = navigator.userAgent;
  
  let device = 'desktop';
  if (/Mobile|Android|iPhone/.test(ua)) device = 'mobile';
  else if (/iPad|Tablet/.test(ua)) device = 'tablet';
  
  let browser = 'autre';
  if (ua.includes('Chrome') && !ua.includes('Edge')) browser = 'Chrome';
  else if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
  else if (ua.includes('Edge')) browser = 'Edge';
  
  let os = 'autre';
  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac')) os = 'macOS';
  else if (ua.includes('Linux') && !ua.includes('Android')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (/iPhone|iPad/.test(ua)) os = 'iOS';
  
  return { device, browser, os };
};

// Récupère les infos de géolocalisation
const getGeoInfo = async () => {
  try {
    // Utilise ipapi.co (gratuit, pas de clé API nécessaire)
    const response = await fetch('https://ipapi.co/json/', { 
      signal: AbortSignal.timeout(3000) // timeout 3s
    });
    const data = await response.json();
    return {
      country: data.country_name || null,
      country_code: data.country_code || null,
      city: data.city || null,
      ip: data.ip || null
    };
  } catch (err) {
    // Si ça échoue, on continue sans géoloc
    return { country: null, country_code: null, city: null, ip: null };
  }
};

// Récupère la source (UTM ou referrer)
const getSource = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const utmSource = urlParams.get('utm_source');
  const utmMedium = urlParams.get('utm_medium');
  const utmCampaign = urlParams.get('utm_campaign');
  
  if (utmSource) {
    return {
      source: utmSource,
      medium: utmMedium || 'unknown',
      campaign: utmCampaign || null
    };
  }
  
  // Sinon utiliser le referrer
  const referrer = document.referrer;
  if (!referrer) {
    return { source: 'direct', medium: 'none', campaign: null };
  }
  
  try {
    const refUrl = new URL(referrer);
    const domain = refUrl.hostname.replace('www.', '');
    
    // Détecter les sources connues
    if (domain.includes('google')) return { source: 'google', medium: 'organic', campaign: null };
    if (domain.includes('facebook') || domain.includes('fb.com')) return { source: 'facebook', medium: 'social', campaign: null };
    if (domain.includes('instagram')) return { source: 'instagram', medium: 'social', campaign: null };
    if (domain.includes('twitter') || domain.includes('t.co')) return { source: 'twitter', medium: 'social', campaign: null };
    if (domain.includes('tiktok')) return { source: 'tiktok', medium: 'social', campaign: null };
    if (domain.includes('youtube')) return { source: 'youtube', medium: 'social', campaign: null };
    if (domain.includes('linkedin')) return { source: 'linkedin', medium: 'social', campaign: null };
    
    return { source: domain, medium: 'referral', campaign: null };
  } catch {
    return { source: 'unknown', medium: 'unknown', campaign: null };
  }
};

export const useTracking = () => {
  const trackPage = useCallback(async (page) => {
    // Ne pas tracker la page admin
    if (page === 'admin') return;
    
    try {
      const visitorId = getVisitorId();
      const sessionId = getSessionId();
      const { device, browser, os } = getDeviceInfo();
      const sourceInfo = getSource();
      
      // Récupérer géoloc en parallèle
      const geoInfo = await getGeoInfo();

      const { error } = await supabase.from('visits').insert({
        visitor_id: visitorId,
        session_id: sessionId,
        page: page || 'chat',
        referrer: document.referrer || null,
        user_agent: navigator.userAgent.substr(0, 500),
        device_type: device,
        browser: browser,
        os: os,
        country: geoInfo.country,
        country_code: geoInfo.country_code,
        city: geoInfo.city,
        ip_hash: geoInfo.ip ? btoa(geoInfo.ip).substr(0, 20) : null, // Hash simple de l'IP
        source: sourceInfo.source,
        medium: sourceInfo.medium,
        campaign: sourceInfo.campaign
      });

      if (error) {
        console.debug('Tracking error:', error);
      }
    } catch (err) {
      // Silencieux - ne pas bloquer l'app
      console.debug('Tracking failed:', err);
    }
  }, []);

  const trackEvent = useCallback(async (eventName, data = {}) => {
    try {
      await supabase.from('visits').insert({
        visitor_id: getVisitorId(),
        session_id: getSessionId(),
        page: 'event:' + eventName,
        referrer: JSON.stringify(data).substr(0, 500)
      });
    } catch (err) {
      // Silencieux
    }
  }, []);

  return { trackPage, trackEvent };
};

export default useTracking;
